import { Divider, Spin } from 'antd';
import { Empty, Form, Input, Modal, Select, Tree } from '@maxtropy/components';
import type { DataNode, TreeProps, EventDataNode } from 'antd/es/tree';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import _ from 'lodash';
import { EnergyUnitTreeItem, getEnergyUnitTree } from '@/api/energyUnitMapping';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { apiV2EnergyAnalysisListByOuPost, V2EnergyAnalysisListPostResponse } from '@maxtropy/device-customer-apis-v2';
import { on } from 'events';

export type AnalysisUnit = Exclude<V2EnergyAnalysisListPostResponse['list'], undefined>[number];

interface UnitDataNode extends DataNode {
  parentId: string;
  label?: string;
}
interface CustomDataNode extends DataNode {
  label?: string;
}

// 将后端返回的数据格式化为树节点格式
function formatToNodeData(data: EnergyUnitTreeItem[]): UnitDataNode[] {
  return data.map(i => {
    return {
      title: i.name,
      label: i.name, // 添加 label 字段，保证后续读取时有值
      key: i.energyUnitId,
      children: i.children && i.children.length > 0 ? formatToNodeData(i.children) : [],
      disabled: i.disabled,
      parentId: i.parentId,
    };
  });
}

const dataList: { key: React.Key; title: string }[] = [];

// 生成树节点列表，用于搜索
const generateList = (data: DataNode[]) => {
  for (let i = 0; i < data.length; i++) {
    const node = data[i];
    const { key, title } = node;
    dataList.push({ key, title: title as string });
    if (node.children) {
      generateList(node.children);
    }
  }
};

const getParentKey = (key: React.Key, tree: DataNode[]): React.Key => {
  let parentKey: React.Key;
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];
    if (node.children) {
      if (node.children.some(item => item.key === key)) {
        parentKey = node.key;
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children);
      }
    }
  }
  return parentKey!;
};

const getParentIdsByNodeId = (data: DataNode[], nodeId: React.Key, path: React.Key[] = []): React.Key[] => {
  for (const node of data) {
    const id = node.key;
    path.push(node.key);
    if (id === nodeId) return path;
    if (node.children) {
      const findChildren = getParentIdsByNodeId(node.children, nodeId, path);
      if (findChildren?.length) return findChildren;
    }
    path.pop();
  }
  return [];
};

const getIds = (data: DataNode, ids: React.Key[]) => {
  if (!data.disabled) {
    ids.push(data.key);
  }

  if (data.children) {
    for (const child of data.children) {
      getIds(child, ids);
    }
  }
  return ids;
};

type NodeInfo = {
  key: string | null;
  label: any;
  group?: AnalysisUnit[];
  groupLabel?: string;
};
interface Props {
  setOpen: (v: boolean) => void;
  energyUnitIds?: number[];
  linkedId?: number; // 用于默认选中的用能单元ID
  onUnitIdSelect: (nodeInfo: NodeInfo) => void;
  linkedEnergyGroupId?: number; // 用于默认选中的用能分析组ID
}

const BindUnitModal: React.FC<Props> = ({ setOpen, onUnitIdSelect, linkedId, linkedEnergyGroupId }) => {
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>(); // 展开的树节点键
  const [searchValue, setSearchValue] = useState('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [treeNodeData, setTreeNodeData] = useState<UnitDataNode[]>([]);
  const [form] = Form.useForm();
  const energyGroupId = Form.useWatch('energyGroupId', form);
  const [loading, setLoading] = useState<boolean>(false);
  const [unitGroup, setUnitGroup] = useState<AnalysisUnit[]>([]); // 用能分析组列表
  const [unitCheckedKeys, setUnitCheckedKeys] = useState<string | null>(null); // 用能分析组选中键
  const [labelKeys, setLabelKeys] = useState<string | null>(null);

  useEffect(() => {
    // 获取用能分析组（始终使用后端接口）
    apiV2EnergyAnalysisListByOuPost({}).then(res => {
      setUnitGroup((res.list ?? []) as AnalysisUnit[]);
      if (res.list !== undefined && res.list.length > 0) {
        // 优先选中 linkedEnergyGroupId
        if (linkedEnergyGroupId) {
          form.setFieldsValue({ energyGroupId: linkedEnergyGroupId });
        } else {
          form.setFieldsValue({ energyGroupId: res.list[0].id });
        }
      }
    });
  }, [linkedEnergyGroupId]);

  const unitGroupOption = useMemo(() => {
    if (unitGroup.length > 0) {
      return unitGroup.map(i => ({
        label: i.name,
        value: i.id,
      }));
    }
  }, [unitGroup]);

  const treeData = useMemo(() => {
    const loop = (data: UnitDataNode[]): DataNode[] =>
      data.map(item => {
        const strTitle = item.title as string;
        const index = strTitle.indexOf(searchValue);
        const beforeStr = strTitle.substring(0, index);
        const afterStr = strTitle.slice(index + searchValue.length);

        let title: ReactNode =
          index > -1 ? (
            <span>
              {beforeStr}
              <span className={styles.siteTreeSearchValue}>{searchValue}</span>
              {afterStr}
            </span>
          ) : (
            <span>{strTitle}</span>
          );

        if (item.children) {
          return {
            title,
            key: item.key,
            children: loop(item.children as UnitDataNode[]),
            disabled: item.disabled,
            selectable: true,
            label: item.title,
          };
        }
        return {
          title,
          key: item.key,
          disabled: item.disabled,
          selectable: true,
          label: item.title,
        };
      });

    return loop(treeNodeData);
  }, [searchValue, treeNodeData]);

  const onExpand = (newExpandedKeys: React.Key[]) => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const newExpandedKeys = dataList
      .map(item => {
        if (item.title.indexOf(value) > -1) {
          return getParentKey(item.key, treeNodeData);
        }
        return null;
      })
      .filter((item, i, self) => item && self.indexOf(item) === i);
    const allParentKeys = (newExpandedKeys as React.Key[]).map(key => {
      return getParentIdsByNodeId(treeNodeData, key);
    });
    setExpandedKeys(_.uniq(allParentKeys.flat()));
    setSearchValue(value);
    setAutoExpandParent(true);
  };

  const onCheck: TreeProps['onCheck'] = (_, { node }: { node: EventDataNode<CustomDataNode> }) => {
    const currentKey = node.key as string;
    if (unitCheckedKeys === currentKey) {
      setUnitCheckedKeys(null);
    } else {
      setUnitCheckedKeys(currentKey);
    }
    setLabelKeys(node.label as string);
  };

  useEffect(() => {
    if (energyGroupId) {
      setLoading(true);
      getEnergyUnitTree(energyGroupId)
        .then(res => {
          const nodes = formatToNodeData(res.list ?? []);
          setTreeNodeData(nodes);
          dataList.length = 0;
          generateList(nodes);
          setExpandedKeys(dataList.map(i => i.key));
          // 如果存在 linkedId，使用它作为默认选中值
          if (linkedId) {
            setUnitCheckedKeys(String(linkedId));
            // 查找对应的 label
            const findNode = (nodes: UnitDataNode[], targetKey: string): UnitDataNode | null => {
              for (const node of nodes) {
                if (String(node.key) === targetKey) return node;
                if (node.children) {
                  const found = findNode(node.children as UnitDataNode[], targetKey);
                  if (found) return found;
                }
              }
              return null;
            };
            const targetNode = findNode(nodes, String(linkedId));
            if (targetNode) setLabelKeys(targetNode.label as string);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [energyGroupId, linkedId]);

  const onBind = () => {
    // : AnalysisUnit[]
    const grLabel = unitGroup.find(i => i.id === energyGroupId);
    if (!unitCheckedKeys) onUnitIdSelect({ key: null, label: null, group: energyGroupId, groupLabel: undefined });
    else onUnitIdSelect({ key: unitCheckedKeys, label: labelKeys, group: energyGroupId, groupLabel: grLabel?.name });
    setOpen(false);
  };

  return (
    <Modal
      title="关联用能单元"
      open={true}
      onCancel={() => {
        setOpen(false);
      }}
      onOk={onBind}
    >
      <Form form={form} size="middle">
        <Form.Item label="用能分析组" name="energyGroupId">
          <Select placeholder="请选择" showSearch optionFilterProp="label" options={unitGroupOption} />
        </Form.Item>
        <div className={styles.content}>
          <Form.Item
            extra={
              treeData.length > 0 && (
                <span>
                  {/* @ts-ignore */}
                  <ExclamationCircleOutlined style={{ color: 'var(--warning-color)', marginRight: 4 }} />
                  灰色选择框表示无该用能单元权限
                </span>
              )
            }
          >
            <div>
              <Input.Search placeholder="请输入关键字" onChange={onInputChange} />
              <Divider style={{ borderColor: 'rgba(255,255,255,0.15)' }} />

              <Spin spinning={loading}>
                {treeData.length > 0 ? (
                  <div className={styles.treeBackground} style={{ overflow: 'auto', maxHeight: 367, width: '100%' }}>
                    <Tree
                      checkable
                      checkStrictly
                      checkedKeys={unitCheckedKeys ? [+unitCheckedKeys] : []}
                      onExpand={onExpand}
                      expandedKeys={expandedKeys}
                      autoExpandParent={autoExpandParent}
                      treeData={treeData}
                      onCheck={onCheck}
                    />
                  </div>
                ) : (
                  <Empty style={{ maxWidth: 433 }} />
                )}
              </Spin>
            </div>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default BindUnitModal;
